import { useEffect } from 'react';
import { FieldError, useForm } from 'react-hook-form';
import { IDynamicFormProps } from '../DynamicForm';
import { IDynamicFormGroup, IDynamicFormRow } from '../DynamicForm.types';
import FormRow, { REDUNDUNT_FIELD } from '../FormRow';
import { generatePermission } from '../utils/generatePermission';
import { useCheckResponse } from '@src/hooks';
import { Divider } from 'antd';
import clsx from 'clsx';

export function useDynamicForm(props: IDynamicFormProps) {
    const { checkResponse } = useCheckResponse();

    const {
        register,
        handleSubmit,
        control,
        setValue,
        getValues,
        watch,
        clearErrors,
        setError,
        trigger,
        formState,
        reset,
    } = useForm({
        shouldFocusError: true,
        ...props.useFormOptions,
        defaultValues: props.formData,
    });

    const { errors, touchedFields, isSubmitted } = formState;
    const mode = props.useFormOptions?.mode || 'onSubmit';

    // Helper function to get error for a field - only show if field has been touched
    const getFieldError = (fieldName: string): FieldError | undefined => {
        const error = errors[fieldName] as FieldError | undefined;
        if (!error) return undefined;

        // If mode is 'onChange', we still want to filter by touched fields to prevent
        // showing all errors when typing in one field
        // Only show error if field has been touched or form has been submitted
        if (touchedFields[fieldName] || isSubmitted) {
            return error;
        }

        return undefined;
    };

    useEffect(() => {
        if (props.resetFormData && props.formData) {
            reset(props.formData);
        }
    }, [props.resetFormData, props.formData]);

    useEffect(() => {
        if (props.itemId && !props.formData && props.callApi) {
            (async () => {
                try {
                    const data = await props.callApi({ id: props.itemId });
                    const { success } = checkResponse(data);
                    if (success) {
                        reset(data.data);
                    }
                } catch (err) {
                    checkResponse({ error: err });
                }
            })();
        }
    }, [props.itemId, props.callApi, reset]);

    const renderFormGroups = (groups?: IDynamicFormGroup[]) => {
        if (!groups) return null;
        return groups.map((formGroup: IDynamicFormGroup, index: number) => (
            <div
                key={props.formKeyName + 'form_group' + index}
                className={formGroup.wrapperClassName}
            >
                {formGroup.title && (
                    <div className={clsx('mb-4', formGroup.containerClassName)}>
                        <p className="text-textPrimary text-M-16">
                            {formGroup.title}
                        </p>
                    </div>
                )}

                <div className={formGroup.className}>
                    {renderFormRows(formGroup.formRows)}
                </div>

                {formGroup.description && (
                    <p className="text-on-background opacity-50 my-2 text-R-12">
                        {formGroup.description}
                    </p>
                )}
                {index < groups.length - 1 && formGroup.showDivider && (
                    <div className="-mx-4 w-[fill-available]">
                        <Divider className="bg-border" />
                    </div>
                )}
            </div>
        ));
    };

    const renderFormRows = (rows?: IDynamicFormRow[]) => {
        if (!rows) return null;
        return rows.map((formRow, index) => {
            const { isDisabled, isHidden } = generatePermission(
                props.disabledFields,
                props.subscriptionKey,
                formRow.keyName,
                formRow.disabled
            );
            if (isHidden) {
                return null;
            }

            if (
                formState.defaultValues?.[formRow.keyName] === REDUNDUNT_FIELD
            ) {
                return null;
            }

            if (formRow.nestedInputs) {
                const { Component } = formRow;

                return (
                    <Component
                        key={props.formKeyName + '-' + formRow.keyName + index}
                        register={register}
                        watch={watch}
                        errors={
                            !formRow.keyName
                                ? errors
                                : (getFieldError(formRow.keyName) ??
                                  getFieldError(
                                      formRow.keyName.split('.')?.[0]
                                  ))
                        }
                        control={control}
                        setValue={setValue}
                        value={
                            formRow.keyName
                                ? props.formData?.[formRow.keyName]
                                : formState.defaultValues
                        }
                        disabled={formRow.disabled}
                        keyName={formRow.keyName}
                        label={
                            <span className="text-R-16">{formRow.label}</span>
                        }
                        formProps={{ ...formRow?.formProps }}
                        reset={reset}
                    />
                );
            }

            return (
                <div
                    key={props.formKeyName + '-' + formRow.keyName + index}
                    className={formRow.wrapperClassName}
                >
                    <FormRow
                        {...formRow}
                        key={props.formKeyName + '-' + formRow.keyName + index}
                        register={register}
                        registerOptions={formRow.registerOptions}
                        label={
                            <span className="text-R-16">{formRow.label}</span>
                        }
                        control={control}
                        value={props.formData?.[formRow.keyName]}
                        errors={getFieldError(formRow.keyName)}
                        setValue={setValue}
                        getValues={getValues}
                        watch={watch}
                        disabled={props.disableAll || isDisabled}
                        setError={setError}
                        triggerValidation={trigger}
                        clearError={clearErrors}
                        reset={reset}
                    />
                </div>
            );
        });
    };

    const onSubmit = (data: Record<string, any>) => {
        Object.keys(data).forEach((key) => {
            if (data[key] === REDUNDUNT_FIELD) {
                delete data[key];
            }
        });
        props.onHandleSubmit(data);
    };

    /**Renders the form rows */
    const createForm = () => {
        return [
            renderFormGroups(props.formGroups),
            renderFormRows(props.formRows),
        ];
    };

    return {
        getValues,
        onSubmit,
        handleSubmit,
        control,
        createForm,
        reset,
        trigger,
        setError,
        watch,
        register,
        setValue,
        clearErrors,
        formState,
        errors,
    };
}
